import React, { FC, cloneElement, useId } from "react";

import classnames from "classnames";

import { Popover, PopoverPlacement, PopoverTriggerType } from "Cobalt/Popover";
import OverlayTrackingWrapper from "Common/OverlayTrackingWrapper";

import makeBem from "Utils/makeBem";

interface SIOverlayTriggerProps {
  onEnter?: () => void;
  onExit?: () => void;
  trackType?: string;
  trackId?: string;
  className?: string;
  placement?:
    | "auto-start"
    | "auto"
    | "auto-end"
    | "top-start"
    | "top"
    | "top-end"
    | "right-start"
    | "right"
    | "right-end"
    | "bottom-end"
    | "bottom"
    | "bottom-start"
    | "left-end"
    | "left"
    | "left-start";
  trigger?: ("hover" | "click" | "focus")[];
  overlay: React.ReactElement;
  children: React.ReactElement;
  [key: string]: any;
}

/*
This is a wrapper for Popover triggers. It has some default props and it is
wrapped by the OverlayTrackingWrapper for tracking how long the Popover is open.

// TODO:
Uses of this could be cleaned up considerably by allowing the user to skip
passing in a popover overlay, all of the children elements, and the tracking
categorization, and simply pass in a category key and the location, and it can
look up what the track categories, tooltip text, and base text should be.
*/
const bem = makeBem("SIOverlayTrigger");

const mapPlacement = (placement: SIOverlayTriggerProps["placement"]): PopoverPlacement => {
  const side = placement?.split("-")[0];
  if (side === "top") return "up";
  if (side === "bottom") return "down";
  if (side === "left") return "left";
  return "right";
};

const mapTrigger = (trigger: SIOverlayTriggerProps["trigger"]): PopoverTriggerType =>
  trigger?.includes("click") && !trigger.includes("hover") ? "click" : "hover";

const isPopoverElement = (node: React.ReactNode): node is React.ReactElement<any> =>
  React.isValidElement(node) && node.type && (node.type as any).displayName === "Popover";

const SIOverlayTrigger: FC<SIOverlayTriggerProps> = ({
  onEnter,
  onExit,
  trackType,
  trackId,
  className,
  placement = "right",
  trigger = ["hover", "focus"],
  overlay,
  children,
  ...rest
}) => {
  const targetId = `si-overlay-trigger-${useId().replace(/:/g, "")}`;
  const popoverPlacement = mapPlacement(placement);
  const popoverTrigger = mapTrigger(trigger);
  // We need to do this to be compatible with our current CSS
  const childWithClass = cloneElement(children, {
    className: classnames(children.props.className, bem(), className),
  });
  const popover = isPopoverElement(overlay) ? (
    cloneElement(overlay, {
      id: targetId,
      placement: popoverPlacement,
      showBackdrop: false,
    })
  ) : (
    <Popover id={targetId} placement={popoverPlacement} showBackdrop={false} body>
      {overlay}
    </Popover>
  );
  const triggerElement = (
    <Popover.Trigger as="span" target={targetId} trigger={popoverTrigger} placement={popoverPlacement} className={className} {...rest}>
      {childWithClass}
    </Popover.Trigger>
  );

  if (trackType || trackId || onEnter || onExit) {
    return (
      <OverlayTrackingWrapper onEnter={onEnter} onExit={onExit} trackType={trackType} trackId={trackId}>
        {triggerElement}
        {popover}
      </OverlayTrackingWrapper>
    );
  }

  return (
    <>
      {triggerElement}
      {popover}
    </>
  );
};

export default SIOverlayTrigger;
